import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type { NewsPaperDoc } from './newsPapers';

const BATCH_LIMIT = 400;

export async function togglePaperLike(
  db: Firestore,
  args: {
    paperId: string;
    viewerUid: string;
    currentlyLiked: boolean;
  }
): Promise<boolean> {
  const likeRef = doc(db, 'newsPapers', args.paperId, 'likes', args.viewerUid);
  const paperRef = doc(db, 'newsPapers', args.paperId);
  const next = !args.currentlyLiked;

  if (next) {
    await setDoc(likeRef, { userId: args.viewerUid, createdAt: serverTimestamp() });
    await updateDoc(paperRef, { likeCount: increment(1) });
  } else {
    await deleteDoc(likeRef);
    await updateDoc(paperRef, { likeCount: increment(-1) });
  }
  return next;
}

export async function togglePaperResonate(
  db: Firestore,
  args: {
    paperId: string;
    paper: NewsPaperDoc;
    viewerUid: string;
    currentlyResonated: boolean;
  }
): Promise<boolean> {
  const resonateRef = doc(db, 'newsPapers', args.paperId, 'resonates', args.viewerUid);
  const paperRef = doc(db, 'newsPapers', args.paperId);
  const next = !args.currentlyResonated;

  if (next) {
    await setDoc(resonateRef, { userId: args.viewerUid, createdAt: serverTimestamp() });
    await updateDoc(paperRef, { resonateCount: increment(1) });

    // Create a Post doc
    const now = serverTimestamp();
    const postRef = await addDoc(collection(db, 'posts'), {
      authorId: args.viewerUid,
      labId: null,
      institutionId: null,
      type: 'paper',
      title: args.paper.title,
      content: args.paper.abstract.slice(0, 500),
      attachments: [{ url: args.paper.url, type: 'link', name: 'arXiv' }],
      tags: [],
      researchArea: args.paper.researchArea,
      likeCount: 0,
      resonateCount: 0,
      viewCount: 0,
      commentCount: 0,
      visibility: 'public',
      isPendingApproval: false,
      sourcePaperId: args.paperId,
      createdAt: now,
      updatedAt: now,
    });

    const newPostId = postRef.id;

    // Write to viewer's own feed
    await setDoc(doc(db, 'feed', args.viewerUid, 'items', newPostId), {
      postId: newPostId,
      authorId: args.viewerUid,
      createdAt: now,
    });

    // Fan out to followers
    const resonatorSnap = await getDoc(doc(db, 'users', args.viewerUid));
    if (resonatorSnap.exists()) {
      const followers: string[] =
        (resonatorSnap.data() as { followers?: string[] }).followers ?? [];

      let batch = writeBatch(db);
      let ops = 0;

      for (const followerId of followers) {
        if (followerId === args.viewerUid) continue;
        const itemRef = doc(db, 'feed', followerId, 'items', newPostId);
        batch.set(
          itemRef,
          {
            postId: newPostId,
            authorId: args.viewerUid,
            createdAt: now,
          },
          { merge: true }
        );
        ops++;
        if (ops >= BATCH_LIMIT) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }

      if (ops > 0) await batch.commit();
    }
  } else {
    await deleteDoc(resonateRef);
    await updateDoc(paperRef, { resonateCount: increment(-1) });
  }
  return next;
}

export async function togglePaperBookmark(
  db: Firestore,
  args: {
    paperId: string;
    viewerUid: string;
    currentlyBookmarked: boolean;
    paper: NewsPaperDoc;
  }
): Promise<boolean> {
  const bookmarkRef = doc(db, 'users', args.viewerUid, 'paperBookmarks', args.paperId);
  const next = !args.currentlyBookmarked;

  if (next) {
    await setDoc(bookmarkRef, {
      paperId: args.paperId,
      title: args.paper.title,
      url: args.paper.url,
      researchArea: args.paper.researchArea,
      publishedAt: args.paper.publishedAt,
      savedAt: serverTimestamp(),
    });
  } else {
    await deleteDoc(bookmarkRef);
  }
  return next;
}
